import { useState } from 'react';
import { Pencil, Trash2, Check, X, Plus } from 'lucide-react';
import type { Site } from '../types';

interface EditableSiteCardProps {
  site?: Site;
  isAddNew?: boolean;
  isEditMode?: boolean;
  onSave: (site: Site) => void;
  onDelete?: (id: string) => void;
  onCancel?: () => void;
}

export default function EditableSiteCard({ 
  site, 
  isAddNew = false, 
  isEditMode = false,
  onSave, 
  onDelete, 
  onCancel 
}: EditableSiteCardProps) {
  const [isEditing, setIsEditing] = useState(isAddNew);
  const [formData, setFormData] = useState<Site>(
    site || { id: '', title: '', subtitle: '', description: '', url: '' }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isValidUrl = (url: string): boolean => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Name is required';
    }
    if (!formData.url.trim()) {
      newErrors.url = 'Link is required';
    } else if (!isValidUrl(formData.url)) {
      newErrors.url = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    
    const siteData: Site = {
      ...formData,
      id: site?.id || Date.now().toString(),
      title: formData.title.trim(),
      subtitle: formData.subtitle.trim(),
      description: formData.description.trim(),
      url: formData.url.trim(),
    };
    
    onSave(siteData);
    setIsEditing(false);
    if (isAddNew) {
      setFormData({ id: '', title: '', subtitle: '', description: '', url: '' });
    }
  };

  const handleDelete = () => {
    if (site && onDelete && window.confirm('Delete this site?')) {
      onDelete(site.id);
    }
  };

  const handleCancel = () => {
    if (isAddNew) {
      onCancel?.();
    } else {
      setFormData(site || { id: '', title: '', subtitle: '', description: '', url: '' });
      setIsEditing(false);
      setErrors({});
    }
  };

  if (isEditing) {
    return (
      <article className="card editing">
        <div className="card-dots">
          <span className="card-dot" />
          <span className="card-dot" />
          <span className="card-dot" />
        </div>
        <div className="card-content">
          <div className="edit-form">
            <div className="form-field">
              <input
                type="text"
                placeholder="Site Name *"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: '' });
                }}
                className={errors.title ? 'error' : ''}
              />
              {errors.title && <span className="field-error">{errors.title}</span>}
            </div>
            
            <input
              type="text"
              placeholder="Subtitle (optional)"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
            />
            
            <textarea
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
            
            <div className="form-field">
              <input
                type="url"
                placeholder="Link URL * (https://...)"
                value={formData.url}
                onChange={(e) => {
                  setFormData({ ...formData, url: e.target.value });
                  if (errors.url) setErrors({ ...errors, url: '' });
                }}
                className={errors.url ? 'error' : ''}
              />
              {errors.url && <span className="field-error">{errors.url}</span>}
            </div>
            
            <div className="edit-actions">
              <button onClick={handleSave} className="btn-save">
                <Check size={16} /> Save
              </button>
              <button onClick={handleCancel} className="btn-cancel">
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  }

  if (!site) return null;

  return (
    <article className="card">
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        {isEditMode && (
          <div className="edit-controls">
            <button onClick={() => setIsEditing(true)} className="btn-icon-edit" title="Edit">
              <Pencil size={14} />
            </button>
            <button onClick={handleDelete} className="btn-icon-delete" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
        <h3 className="card-title">{site.title}</h3>
        <p className="card-subtitle">{site.subtitle}</p>
        <p className="card-description">{site.description}</p>
        <a 
          href={site.url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="card-link"
        >
          <span>Visit Site</span>
        </a>
      </div>
    </article>
  );
}

// Add new site button/card
export function AddSiteButton({ onClick }: { onClick: () => void }) {
  return (
    <article className="card add-new-card" onClick={onClick}>
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        <div className="add-new-content">
          <Plus size={32} />
          <span>Add New Site</span>
        </div>
      </div>
    </article>
  );
}
